import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, KeyRound, UserPlus, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';

interface UserItem {
  id: string;
  email: string;
  role: 'admin' | 'reviewer' | 'auditor' | 'viewer';
  full_name: string | null;
  created_at: string;
  updated_at: string;
}

const ROLES: UserItem['role'][] = ['admin', 'reviewer', 'auditor', 'viewer'];

export function UsersPage() {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-user form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserItem['role']>('reviewer');
  const [submitting, setSubmitting] = useState(false);

  // Reset-password modal state
  const [resetTarget, setResetTarget] = useState<UserItem | null>(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetting, setResetting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await api.get<UserItem[]>('/api/v1/users/');
      setUsers(list);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load users.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void loadUsers(); }, [loadUsers]);

  const handleCreate = async () => {
    if (!email.trim() || password.length < 8) {
      toast.error('Email and a password (min 8 chars) are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/v1/users/', {
        email: email.trim(),
        password,
        role,
        full_name: fullName.trim() || null,
      });
      toast.success(`Created user ${email.trim()}.`);
      setEmail(''); setPassword(''); setFullName(''); setRole('reviewer');
      await loadUsers();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to create user.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (user: UserItem, next: UserItem['role']) => {
    if (next === user.role) return;
    try {
      await api.patch(`/api/v1/users/${user.id}`, { role: next });
      toast.success(`Updated ${user.email} to ${next}.`);
      await loadUsers();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to update role.';
      toast.error(msg);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || resetPwd.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setResetting(true);
    try {
      await api.post(`/api/v1/users/${resetTarget.id}/password`, { password: resetPwd });
      toast.success(`Password reset for ${resetTarget.email}.`);
      setResetTarget(null);
      setResetPwd('');
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to reset password.';
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/users/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.email}.`);
      setDeleteTarget(null);
      await loadUsers();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete user.';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Users</h1>
      <p className="text-sm text-muted-foreground">
        Add, remove, and manage tenant members. Users authenticate against Supabase Auth;
        passwords set here are immediately effective.
      </p>

      {/* Add user */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="h-5 w-5" /> Add New User
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Email</Label>
              <Input id="new-email" type="email" autoComplete="off" value={email}
                onChange={(e) => { setEmail(e.target.value); }} placeholder="user@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Full name (optional)</Label>
              <Input id="new-name" value={fullName}
                onChange={(e) => { setFullName(e.target.value); }} placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pwd">Initial password</Label>
              <Input id="new-pwd" type="password" autoComplete="new-password" value={password}
                onChange={(e) => { setPassword(e.target.value); }} placeholder="min 8 characters" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-role">Role</Label>
              <select id="new-role" value={role}
                onChange={(e) => { setRole(e.target.value as UserItem['role']); }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => { void handleCreate(); }} disabled={submitting} size="sm">
              {submitting ? 'Creating…' : 'Create User'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Existing Users</CardTitle></CardHeader>
        <CardContent>
          {isLoading && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Could not load users</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {!isLoading && !error && users.length === 0 && (
            <p className="text-sm text-muted-foreground">No users yet.</p>
          )}
          {!isLoading && !error && users.length > 0 && (
            <ul className="divide-y divide-border">
              {users.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium">{u.full_name || u.email}</span>
                    <span className="text-xs text-muted-foreground">{u.email}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>{u.role}</Badge>
                    <select value={u.role}
                      onChange={(e) => { void handleRoleChange(u, e.target.value as UserItem['role']); }}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Button variant="outline" size="sm"
                      onClick={() => { setResetTarget(u); setResetPwd(''); }}>
                      <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setDeleteTarget(u); }}>
                      <Trash2 className="h-3.5 w-3.5 mr-1 text-destructive" />
                      <span className="text-destructive">Delete</span>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Reset password modal (using AlertDialog as a simple form holder) */}
      <AlertDialog open={resetTarget !== null} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new password for <code>{resetTarget?.email}</code>. The user can sign in with it
              immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input type="password" autoComplete="new-password" placeholder="min 8 characters"
            value={resetPwd} onChange={(e) => { setResetPwd(e.target.value); }} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { void handleResetPassword(); }} disabled={resetting}>
              {resetting ? 'Saving…' : 'Set password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <code>{deleteTarget?.email}</code> from your tenant.
              They lose access immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { void handleDelete(); }} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
